import { WithGatsbyImage } from "@ts/components";
import {
  SanityKeyed,
  ServiceHighlight,
  SettingFindInStore,
  SettingFlashSale,
  SettingServiceHighlights,
  Store,
} from "@ts/sanity";
import { graphql, useStaticQuery } from "gatsby";
import { useLocalisation } from "./useLocalisation";

export type UseSettings = {
  findInStore?: SettingFindInStore;
  allStores?: {
    edges: {
      node: Store;
    }[];
  };
  flashSale?: SettingFlashSale;
  serviceHighlights?: Partial<Omit<SettingServiceHighlights, "items">> & {
    items: WithGatsbyImage<SanityKeyed<ServiceHighlight>, "image">[];
  };
};

export const useSettings = (): UseSettings => {
  const settings = useStaticQuery(graphql`
    query SANITY_SETTINGS {
      allFindInStore: allSanitySettingFindInStore {
        edges {
          node {
            i18n_lang
            i18n_base {
              id
            }
            enabled
          }
        }
      }
      allStores: allSanityStore(
        filter: { enableCollect: { eq: true } }
        sort: { displayOrder: ASC }
      ) {
        edges {
          node {
            ...GatsbyStoreFragment
          }
        }
      }
      flashSale: sanitySettingFlashSale {
        enabled
        title
        description
        slug {
          current
        }
        collections {
          id
        }
      }
      allServiceHighlights: allSanitySettingServiceHighlights {
        edges {
          node {
            i18n_lang
            i18n_base {
              id
            }
            enabled
            delay
            items {
              ...ServiceHighlight
            }
          }
        }
      }
    }
  `);

  const { findNodeByLocale } = useLocalisation();
  const { allFindInStore, allServiceHighlights, ...rest } = settings;

  return {
    ...rest,
    findInStore: findNodeByLocale<SettingFindInStore>(allFindInStore),
    serviceHighlights:
      findNodeByLocale<SettingServiceHighlights>(allServiceHighlights),
  };
};
